<script setup lang="ts">
import RmaRow from './RmaRow.vue';
import UnidentifiedRmaRow from './UnidentifiedRmaRow.vue';
import {computed, inject, provide, ref, watchEffect} from 'vue';
import {
  apps,
  formatting,
  LegacyAdjustment,
  LegacyAdjustmentCategory,
  LegacyOrder,
  legacyOrderCommands,
  legacyOrderKey,
  LegacyRma,
  legacyRmaKey,
  LegacyRule,
  useDialogs,
  useLocalization
} from '@hantera/portal-app';
import {ContextMenuButton, Panel} from '@hantera/design-system';
import useBackend from '@/composition/useBackend';
import RmaAdjustmentForm from './RmaAdjustmentForm.vue';
import Decimal from 'decimal.js';
import {v4 as uuidv4} from 'uuid';
import RmaAdjustment from './RmaAdjustment.vue';
import {EditableOrderRule} from '@/components/LegacyOrder/editable-order-adjustment';
import AppSlotView from '@/AppSlotView.vue';

const props = defineProps<{
  data: LegacyRma
}>();

provide(legacyRmaKey, props.data);

const backend = useBackend();
const order = inject(legacyOrderKey, ref<LegacyOrder>({} as any));
const { currency } = formatting;
const t = useLocalization();
const showAddAdjustmentForm = ref(false);
const dialogs = useDialogs()

const newAdjustmentFormElem = ref<typeof RmaAdjustmentForm>();
const newAdjustmentCategory = ref<LegacyAdjustmentCategory>();
const newAdjustmentValue = ref(new Decimal(0));

const expanded = ref(false);

const manualAdjustments = ref<EditableOrderRule[]>([]);
const ruleAdjustments = ref<EditableOrderRule[]>([]);

watchEffect(() => {
  // Manual adjustments can be found in the rma rules
  manualAdjustments.value = props.data.rules.map(r => {
    const value = r.ruleTotal.neg();

    return {
      adjustmentId: r.orderRuleId,
      editable: props.data.state !== 'Completed',
      name: r.name,
      originalValue: value,
      value: value,
      affectedRows: []
    };
  });

  function isNotManual(a: LegacyAdjustment) {
    return !manualAdjustments.value.some(r => r.adjustmentId == a.orderRule?.orderRuleId);
  }


  const adjustmentsFromRowsThatAreNotRowBased = props.data.rows?.flatMap((r) => {
    return (r.adjustments || [])
      .concat(r.exchange?.adjustments || [])
      .filter((a) => !a.fields.some((f) => f.id === 'RowBasedAdjustment'))
  });

  const nonRowAdjustmentsTotals = adjustmentsFromRowsThatAreNotRowBased.concat(props.data.adjustments)
    .reduce((acc, a) => {
      if(!a.orderRule?.orderRuleId) return acc;

      acc[a.orderRule.orderRuleId] = (acc[a.orderRule.orderRuleId] || new Decimal(0)).add(a.adjustmentTotal);
      return acc;
    }, {} as Record<string, Decimal>) || {};

  const orderRules = order.value.rules?.map((r) => (<LegacyRule>{
    ...r,
    actions: [],
    ruleTotal: new Decimal(0),
    ruleVat: new Decimal(0),
    percentage: new Decimal(0)
  })) || [];
  const allRules = orderRules.concat(props.data.rules || []);

  ruleAdjustments.value = <EditableOrderRule[]>Object.entries(nonRowAdjustmentsTotals).map((a) => {
    const rule = allRules.find((r) => r.orderRuleId == a[0]);
    const ruleTotal = a[1].mul(-1);

    if (!rule) return
    
    return <EditableOrderRule>{
      adjustmentId: rule.orderRuleId,
      editable: false,
      name: rule.name,
      originalValue: ruleTotal,
      value: ruleTotal,
      affectedRows: []
    }
  }).filter((r) => r !== undefined && !r.value.eq(0) && manualAdjustments.value.find(m => m.adjustmentId == r.adjustmentId) === undefined)
  
});

function toggleExpand() {
  expanded.value = !expanded.value;
}

const canComplete = computed(() => props.data.state != 'Completed');

const totalRefund = computed(() => props.data.refundTotal)

async function complete() {
  if (order.value.draftChanges?.find(g => g.commands.find(c => c.type === 'CompleteRma') !== undefined) !== undefined) {
    await dialogs.alert("It's not possible to complete two or more RMAs before saving. Please save order first before closing more RMAs")
  } else {
    await backend.legacyOrders.buildCommandGroup(
      order.value.orderId,
      "Complete RMA {rmaNumber}",
      {
        rmaNumber: props.data.rmaNumber
      }
    )
    .command(legacyOrderCommands.completeRma, {
      rmaId: props.data.rmaId
    })
    .send();
  }
}

const canEditAdjustments = computed(() => props.data.state !== 'Completed');

function toggleAddAdjustment() {
  showAddAdjustmentForm.value = !showAddAdjustmentForm.value;
}

async function submitNewAdjustment() {
  if (!newAdjustmentCategory.value) {
    // TODO Add error state
    return;
  }
  if (newAdjustmentValue.value.eq(new Decimal(0))) {
    newAdjustmentFormElem.value?.setValueError();
    return;
  }

  const adjustmentId = uuidv4();

  await backend.legacyOrders.buildCommandGroup(
    order.value.orderId,
    'Add adjustment of {amount} to RMA {rmaNumber}',
    {
      amount: currency(newAdjustmentValue.value, order.value.currencyCode),
      rmaNumber: props.data.rmaNumber,
    }
  )
    .command(legacyOrderCommands.addOrUpdateRmaAdjustment, {
      rmaId: props.data.rmaId,
      rmaAdjustmentId: adjustmentId,
      priceWithVat: newAdjustmentValue.value.neg()
    })
    // Legacy action sets this field but doesn't seem to be any UI for it?
    // .command(upsertRmaAdjustmentField, {
    //   rmaId: props.data.rmaId,
    //   rmaAdjustmentId: adjustmentId,
    //   fieldKey: 'Description',
    //   fieldValue: '',
    // })
    .command(legacyOrderCommands.upsertRmaAdjustmentField, {
      rmaId: props.data.rmaId,
      rmaAdjustmentId: adjustmentId,
      fieldKey: 'CategoryCode',
      fieldValue: newAdjustmentCategory.value.code,
    })
    .command(legacyOrderCommands.upsertRmaAdjustmentField, {
      rmaId: props.data.rmaId,
      rmaAdjustmentId: adjustmentId,
      fieldKey: 'CategoryDescription',
      fieldValue: newAdjustmentCategory.value.name,
    })
    .command(legacyOrderCommands.addActivityLog, {
      message: 'Added adjustment {adjustmentName} with value {adjustmentValue} to RMA {rmaNumber}',
      fields: {
        adjustmentName: newAdjustmentCategory.value.name,
        adjustmentValue: currency(newAdjustmentValue.value, order.value.currencyCode),
        rmaNumber: props.data.rmaNumber,
      },
    })
    .send();
  newAdjustmentCategory.value = undefined;
  newAdjustmentValue.value = new Decimal(0);
  showAddAdjustmentForm.value = false;
}

function cancelNewAdjustment() {
  newAdjustmentCategory.value = undefined;
  newAdjustmentValue.value = new Decimal(0);
  showAddAdjustmentForm.value = false;
}

const headerDate = computed(() => {
  if (props.data.state === 'Completed' && props.data.completedUtc) {
    return 'Completed: ' + formatting.dateTime(props.data.completedUtc)
  }
  return 'Created: ' + formatting.dateTime(props.data.createdUtc)
})

</script>

<template>
  <Panel class="rma">

    <div class="rma__header">
      <div
        class="rma__state"
        :style="`background-color: var(--state-colors__rma-${data.state.toLowerCase()})`"
        :title="data.state"
      ></div>
      <div class="rma__header-details">
        <div class="rma__title">
          <div
            class="rma__number"
            data-sentry-unmask
          >{{ data.rmaNumber }}</div>
        </div>
        <div class="rma__date">
          {{ headerDate }}
        </div>
      </div>
      <div
        class="rma__header-button"
        v-if="canComplete"
      >
        <button
          :class="{
            'btn-primary': data.state === 'PackageReceived'
          }"
          @click="complete"
          data-sentry-unmask
        >{{ t('orderView.rma.complete') }}</button>
      </div>
      <ContextMenuButton class="rma__context-menu">
        <AppSlotView :slot="apps.legacySlots.legacyOrder.rma.contextMenu" />
      </ContextMenuButton>
    </div>

    <RmaRow
      v-for="row in data.rows"
      :key="row.rmaRowId"
      :rma="data"
      :row="row"
    />

    <UnidentifiedRmaRow
      v-for="row in data.unidentifiedProducts"
      :key="row.id"
      :rma="data"
      :row="row"
    />

    <AppSlotView :slot="apps.legacySlots.legacyOrder.rma.rows" />

    <div class="rma__summary-row">
      <div
        class="rma__expander"
        :class="{ 'rma__expander--expanded': expanded }"
        @click="toggleExpand"
      >
        <!-- <IconChevron /> Details -->
      </div>
      <div class="rma__summary">
        <RmaAdjustment
          v-for="adjustment in ruleAdjustments"
          :key="adjustment.adjustmentId"
          :rma="data"
          :adjustment="adjustment"
        />
        <RmaAdjustment
          v-for="adjustment in manualAdjustments"
          :key="adjustment.adjustmentId"
          :rma="data"
          :adjustment="adjustment"
        />


        <div class="rma__add-adjustment">
          <a
            href="#"
            class="btn-editable"
            v-if="canEditAdjustments && !showAddAdjustmentForm"
            @click.prevent="toggleAddAdjustment"
          >
            {{ t('orderView.rma.addAdjustment') }}
          </a>
        </div>
        <RmaAdjustmentForm
          class="rma__add-adjustment"
          v-if="showAddAdjustmentForm"
          ref="newAdjustmentFormElem"
          v-model:selected-category="newAdjustmentCategory"
          v-model:value="newAdjustmentValue"
          @submit="submitNewAdjustment"
          @cancel="cancelNewAdjustment"
        />
        <div class="rma__total-sum rma__total-sum-label">{{ t('orderView.rma.totalRefund') }}</div>
        <div class="rma__total-sum rma__total-sum-value">
          {{ currency(totalRefund, order.currencyCode) }}
        </div>
      </div>
    </div>

    <div v-if="expanded">
      <AppSlotView :slot="apps.legacySlots.legacyOrder.rma.details" />
    </div>

    <div class="rma__footer">
      <AppSlotView :slot="apps.legacySlots.legacyOrder.rma.footer" />
    </div>
  </Panel>
</template>

<style>
.rma {
  max-width: 800px;
}

.rma__header {
  position: relative;
  padding-left: 10px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.rma__state {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  border-radius: 5px;
}

.rma__header-details {
  flex-grow: 1;
}

.rma__title {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min-content;
  gap: 10px;
  align-items: center;
}

.rma__number {
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
}

.rma__header-button {
  margin-right: 5px;
}

.rma__context-menu {
  height: 32px;
  width: 32px;
}

.rma__tags {
  display: flex;
}

.rma__summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px;
}

.rma__summary {
  display: grid;
  grid-template-columns: max-content min-content;
  column-gap: 10px;
  row-gap: 5px;
  text-align: right;
}

.rma__total-sum {
  font-weight: 600;
}

.rma__add-adjustment {
  grid-column: 1 / 3;
}

.rma__expander {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.rma__expander svg {
  width: 14px;
  margin-right: 8px;
}

.rma__expander--expanded svg {
  transform: rotate(180deg);
}

.rma__footer {
  display: flex;
}

.rma__footer-title {
  font-weight: 500;
  margin: 10px 0 5px;
}

.rma__footer-extensions {
  width: 50%;
}
</style>